// USAJOBS access + federal-job mapping for JobsPage.
//
// Every USAJOBS request goes through the `usajobs-search` Supabase Edge
// Function. The browser never holds the USAJOBS authorization key or the
// registered email — those live in server-side function secrets (see
// supabase/functions/usajobs-search/index.ts). Do not add a direct fetch
// to the USAJOBS API host here or anywhere else in src/ — the credential
// scan in usajobs.test.js will fail the suite if you do.
import { supabase } from './supabaseClient';

export function formatSalary(low, high) {
  const fmt = n => n >= 1000 ? `$${(n/1000).toFixed(0)}k` : `$${n}`;
  if (low && high) return `${fmt(low)} – ${fmt(high)}`;
  if (low) return `${fmt(low)}+`;
  return 'Salary not listed';
}

export function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7)  return `${diff}d ago`;
  if (diff < 30) return `${Math.floor(diff/7)}w ago`;
  return `${Math.floor(diff/30)}mo ago`;
}

// Builds the minimal whitelisted parameter object the proxy accepts. The
// Edge Function re-validates everything server-side; this keeps the request
// body down to only what this search actually needs. Location rules mirror
// Phase E: Remote searches are not local (no location/radius), and a
// location only narrows results when paired with a real radius.
export function buildUsaJobsSearchRequest({
  keyword,
  locationName,
  radius,
  remoteOnly,
  categoryCode,
  sortBySalary,
  resultsPerPage,
} = {}) {
  const request = {};
  if (keyword) request.keyword = keyword;
  if (categoryCode) request.categoryCode = categoryCode;
  if (remoteOnly) {
    request.remoteOnly = true;
  } else if (radius && radius !== 'Nationwide' && locationName) {
    request.locationName = locationName;
    request.radius = String(radius);
  }
  if (sortBySalary) request.sortBySalary = true;
  if (resultsPerPage) request.resultsPerPage = resultsPerPage;
  return request;
}

// Runs one search through the proxy. Returns { data, error } in the same
// shape supabase-js uses; `data` is the untouched USAJOBS response body.
export async function searchUsaJobs(request) {
  const { data, error } = await supabase.functions.invoke('usajobs-search', {
    body: request,
  });
  if (error) return { data: null, error };
  return { data, error: null };
}

export function mapJob(item, matchReason = null) {
  const j = item.MatchedObjectDescriptor;
  if (!j) return null;
  const salLow  = parseFloat(j.PositionRemuneration?.[0]?.MinimumRange) || 0;
  const salHigh = parseFloat(j.PositionRemuneration?.[0]?.MaximumRange) || 0;
  const remoteRaw  = j.PositionOfferingType?.find(t =>
    t.Name?.toLowerCase().includes('remote') || t.Name?.toLowerCase().includes('telework')
  );
  const locDisplay = j.PositionLocationDisplay || '';
  const isRemote   = !!remoteRaw || locDisplay.toLowerCase().includes('anywhere');
  const remoteLabel = isRemote ? 'Remote' : locDisplay.toLowerCase().includes('hybrid') ? 'Hybrid' : 'On-site';

  const quals      = j.QualificationSummary || '';
  const title      = j.PositionTitle || '';
  const jobSummary = j.UserArea?.Details?.JobSummary || '';
  const searchText = `${quals} ${title} ${jobSummary}`.toLowerCase();

  const hasTSSCI = ['ts/sci','top secret/sci','top secret / sci','tssci','ts-sci','sci clearance','sci eligible','sensitive compartmented','polygraph','ci poly','full scope poly','lifestyle poly'].some(k => searchText.includes(k));
  const hasSecret = !hasTSSCI && ['secret clearance','active secret','secret security clearance','minimum secret','requires secret','hold a secret','obtain a secret','secret or higher'].some(k => searchText.includes(k));
  const hasPublicTrust = !hasTSSCI && !hasSecret && ['public trust','mbi','moderate risk','high risk','background investigation'].some(k => searchText.includes(k));

  // Clearance here is VetQualified's keyword scan of the listing text, not
  // an official USAJOBS field. When the text supports nothing, say so —
  // never default to a plausible-looking level like 'Public Trust'.
  const clearanceInferred = hasTSSCI || hasSecret || hasPublicTrust;
  const clearance     = hasTSSCI ? 'TS/SCI' : hasSecret ? 'Secret' : hasPublicTrust ? 'Public Trust' : 'Not specified';
  const clearanceCode = hasTSSCI ? 'tssci'  : hasSecret ? 'secret' : hasPublicTrust ? 'public' : 'unspecified';

  const tags = [];
  const techWords = ['Python','Splunk','SIEM','Cloud','AWS','Linux','Windows','Network','Cyber','RMF','NIST','Zero Trust','Firewall','DevSecOps'];
  techWords.forEach(w => { if (quals.includes(w) || j.PositionTitle?.includes(w)) tags.push(w); });

  // D-saved-jobs Phase 2 prep: use MatchedObjectId (top-level, numeric, unique)
  // as the canonical job identifier instead of PositionID (announcement string,
  // non-unique, agency-formatted). MatchedObjectId is what saved_jobs persists,
  // and what refetch logic uses to recover saved jobs not in the current
  // result set.
  return {
    id: item.MatchedObjectId, title: j.PositionTitle, company: j.OrganizationName,
    source: 'usajobs',
    logo: (j.OrganizationName || 'US').slice(0,2).toUpperCase(), logoColor: '#1a4a8a',
    location: locDisplay, remote: remoteLabel, clearance, clearanceCode, clearanceInferred,
    salary: formatSalary(salLow, salHigh), type: 'Full-time',
    posted: timeAgo(j.PublicationStartDate),
    applyUrl: j.ApplyURI?.[0] || j.PositionURI || `https://www.usajobs.gov/job/${item.MatchedObjectId}`,
    // PositionURI is kept separately from applyUrl so "View on USAJOBS"
    // always lands on the listing itself, even when ApplyURI points at an
    // agency application system.
    positionUri: j.PositionURI || `https://www.usajobs.gov/job/${item.MatchedObjectId}`,
    description: quals,
    tags: tags.slice(0, 5), matchReasons: matchReason ? [matchReason] : [],
  };
}

// D-saved-jobs Phase 2: refetch a single saved job by MatchedObjectId.
// USAJobs supports Keyword=<MatchedObjectId> as a search; the API probe
// confirmed this returns exactly 1 exact match for valid IDs. We validate
// the returned MatchedObjectId before mapping to defend against future
// API changes or edge cases. When a saved job is no longer findable
// (listing expired, ID wrong, or Keyword search doesn't surface it) —
// or the proxy call fails — return a tombstone so the cache records the
// attempt and the UI can render an "unavailable" state instead of silently
// dropping the entry.
export async function fetchJobByMatchedObjectId(moid) {
  if (!moid) return null;
  const { data, error } = await searchUsaJobs({ keyword: String(moid), resultsPerPage: 1 });
  if (error) return { id: moid, unavailable: true };
  const item = data?.SearchResult?.SearchResultItems?.[0];
  if (!item || String(item.MatchedObjectId) !== String(moid)) {
    return { id: moid, unavailable: true };
  }
  return mapJob(item);
}
